import { useMonitoringConfigIntl } from '@oc/pages/settings/monitoring/translations/monitoringConfigIntl';
import { useStaticPermissions } from 'common/auth/Authorize';
import { useFeatureFlag } from 'common/featureFlag/useFeatureFlag';
import { NavItem } from 'src/components/common/HorizontalNavPage';
import { useTenantConfig } from 'utils/hooks/useTenantConfig';
import { MONITOR_TAB } from './Monitoring.types';
import { filterNotEmpty } from '@beeline.com/bp-design-system';

export function useMonitoringNavItems() {
  const staticPermission = useStaticPermissions();
  const tenantConfig = useTenantConfig();
  const isAuditPermission = staticPermission({ permissionName: 'AUDIT_REPORT' }).READ;
  const isWorkdayImportPermission = staticPermission({ permissionName: 'WORKDAY_IMPORT' }).READ;
  const isUkgImportPermission = staticPermission({ permissionName: 'UKG_IMPORT' }).READ;
  const isUcTransformOutputPermission = staticPermission({ permissionName: 'UC_TRANSFORM_OUTPUT' }).READ;
  const isTimesheetImportPermission = staticPermission({
    permissionName: 'MONITORING_WORKDAY_TIME_ALLOCATION_IMPORT'
  }).READ;
  const isSettingsSystemActivitiesPermission = staticPermission({ permissionName: 'SETTINGS_SYSTEM_ACTIVITIES' }).READ;
  const isAuditFeatureFlag = useFeatureFlag('audit-reports');
  const intl = useMonitoringConfigIntl();
  const hasImportPermission = isSettingsSystemActivitiesPermission;
  const isAuditEnabled = isAuditPermission && isAuditFeatureFlag;
  const isWorkdayImportEnabled = isWorkdayImportPermission && isSettingsSystemActivitiesPermission;
  const isUkgEnabled = tenantConfig?.tenantConfig?.ukgConfig?.enabled === true;
  const isUkgImportEnabled = isUkgEnabled && isUkgImportPermission && isSettingsSystemActivitiesPermission;
  const isUcTransformOutputEnabled = isUcTransformOutputPermission && isSettingsSystemActivitiesPermission;
  const isWorkdayTimeAllocationImportEnabled = isTimesheetImportPermission && isSettingsSystemActivitiesPermission;
  const isInvoiceImportEnabled = isSettingsSystemActivitiesPermission;

  return {
    flags: {
      isInvoiceImportEnabled,
      isWorkdayTimeAllocationImportEnabled,
      isUkgImportEnabled,
      isWorkdayImportEnabled,
      isUcTransformOutputEnabled,
      hasImportPermission,
      isAuditEnabled
    },
    navItems: filterNotEmpty<NavItem>([
      hasImportPermission && {
        label: intl.importLogs,
        path: MONITOR_TAB.IMPORT_LOGS,
        permissions: [{ name: 'SETTINGS_SYSTEM_ACTIVITIES', action: 'READ' }]
      },
      isUcTransformOutputEnabled && {
        label: intl.recipeExportLogs,
        path: MONITOR_TAB.UC_TRANSFORM_OUTPUTS,
        permissions: [
          { name: 'SETTINGS_SYSTEM_ACTIVITIES', action: 'READ' },
          { name: 'UC_TRANSFORM_OUTPUT', action: 'READ' }
        ]
      },
      isAuditEnabled && {
        label: intl.auditReports,
        featureFlag: 'audit-reports',
        path: MONITOR_TAB.AUDIT,
        permissions: [{ name: 'AUDIT_REPORT', action: 'READ' }]
      },
      isUkgImportEnabled && {
        label: intl.ukgImport,
        path: MONITOR_TAB.UKG_IMPORTS,
        permissions: [
          { name: 'SETTINGS_SYSTEM_ACTIVITIES', action: 'READ' },
          { name: 'UKG_IMPORT', action: 'READ' }
        ]
      },
      isWorkdayImportEnabled && {
        label: intl.workdayImports,
        path: MONITOR_TAB.WORKDAY_IMPORTS,
        permissions: [
          { name: 'SETTINGS_SYSTEM_ACTIVITIES', action: 'READ' },
          { name: 'WORKDAY_IMPORT', action: 'READ' }
        ]
      },

      isWorkdayTimeAllocationImportEnabled && {
        label: intl.workdayTimeAllocationImports,
        path: MONITOR_TAB.WORKDAY_TIME_ALLOCATION_IMPORTS,
        permissions: [
          { name: 'SETTINGS_SYSTEM_ACTIVITIES', action: 'READ' },
          { name: 'MONITORING_WORKDAY_TIME_ALLOCATION_IMPORT', action: 'READ' }
        ]
      },
      isInvoiceImportEnabled && {
        label: intl.invoiceImport,
        path: MONITOR_TAB.INVOICE_IMPORTS,
        permissions: [{ name: 'SETTINGS_SYSTEM_ACTIVITIES', action: 'READ' }]
      }
    ])
  };
}
